/**
 * GoalDetailSheet Component (Phase 21.3.4)
 *
 * Full-screen BottomSheet showing goal progress, assessment history,
 * and action buttons (change target, abandon, delete).
 *
 * Opened when an operator taps a GoalCard. Uses useGoalHistory hook
 * for lazy-loaded assessment data (only fetches when sheet is open).
 *
 * Action states are managed by a single actionMode enum for mutual exclusivity.
 * Once historyData loads, prefers historyData.goal over the prop goal for
 * score/trend display (fresher data).
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2,
  AlertCircle,
  History,
  ArrowRight,
} from 'lucide-react';
import type { OperatorGoalResponse } from '@guild/shared';
import { BottomSheet } from './BottomSheet';
import {
  useGoalHistory,
  useUpdateGoal,
  useDeleteGoal,
  operatorGoalKeys,
} from '../hooks/useOperatorGoals';
import type { GoalAssessmentHistoryItem } from '../hooks/useOperatorGoals';
import { getRubricLabel } from '../utils';
import { getScoreColor } from '../../../lib/score-colors';
import { cn } from '../../../lib/utils';

// ============================================
// TYPES
// ============================================

interface GoalDetailSheetProps {
  /** Goal ID to fetch history for */
  goalId: string | null;
  /** Cached goal from list (may be stale — history endpoint provides fresher data) */
  goal: OperatorGoalResponse | null;
  /** Sheet open state */
  isOpen: boolean;
  /** Close callback */
  onClose: () => void;
}

type ActionMode = 'none' | 'editTarget' | 'confirmAbandon' | 'confirmDelete';

// Target score options (same as CreateGoalSheet)
const TARGET_OPTIONS = [
  { value: 60, label: 'Developing' },
  { value: 80, label: 'Proficient' },
  { value: 100, label: 'Exemplary' },
] as const;

// Date formatter for assessment history
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

// ============================================
// MAIN COMPONENT
// ============================================

export function GoalDetailSheet({ goalId, goal: propGoal, isOpen, onClose }: GoalDetailSheetProps) {
  const queryClient = useQueryClient();
  const [actionMode, setActionMode] = useState<ActionMode>('none');
  const [editTarget, setEditTarget] = useState<number>(80);

  // Lazy-load history when sheet opens
  const { data: historyData, isLoading, isError, refetch } = useGoalHistory(goalId);

  // Prefer historyData.goal (fresher) over propGoal once loaded
  const goal = historyData?.goal ?? propGoal;
  const history = historyData?.history ?? [];

  // Mutations
  const updateGoal = useUpdateGoal();
  const deleteGoal = useDeleteGoal();

  // Reset action mode when sheet closes
  const handleClose = useCallback(() => {
    setActionMode('none');
    onClose();
  }, [onClose]);

  // Start editing target — initialize with current target
  const handleStartEditTarget = useCallback(() => {
    setActionMode('editTarget');
    setEditTarget(goal?.targetScore ?? 80);
  }, [goal?.targetScore]);

  // Save new target
  const handleSaveTarget = useCallback(() => {
    if (!goalId) return;
    updateGoal.mutate(
      { goalId, data: { targetScore: editTarget as 0 | 20 | 40 | 60 | 80 | 100 } },
      {
        onSuccess: () => {
          setActionMode('none');
          // Invalidate history cache so the sheet refreshes with new target
          queryClient.invalidateQueries({ queryKey: operatorGoalKeys.history(goalId) });
        },
      },
    );
  }, [goalId, editTarget, updateGoal, queryClient]);

  // Abandon goal
  const handleAbandon = useCallback(() => {
    if (!goalId) return;
    updateGoal.mutate(
      { goalId, data: { status: 'abandoned' } },
      {
        onSuccess: () => {
          setActionMode('none');
          // Invalidate history cache to refresh goal status
          queryClient.invalidateQueries({ queryKey: operatorGoalKeys.history(goalId) });
        },
      },
    );
  }, [goalId, updateGoal, queryClient]);

  // Delete goal — remove history cache and close sheet on success
  const handleDelete = useCallback(() => {
    if (!goalId) return;
    // Remove history cache immediately to prevent stale refetch hitting 404
    queryClient.removeQueries({ queryKey: operatorGoalKeys.history(goalId) });
    deleteGoal.mutate(goalId, {
      onSuccess: () => {
        handleClose();
      },
    });
  }, [goalId, deleteGoal, handleClose, queryClient]);

  if (!goal) {
    return (
      <BottomSheet isOpen={isOpen} onClose={handleClose} snapTo="full">
        <LoadingSkeleton />
      </BottomSheet>
    );
  }

  const isActive = goal.status === 'active';
  const isCompleted = goal.status === 'completed';
  const isAbandoned = goal.status === 'abandoned';
  const hasScore = goal.currentScore !== null && goal.currentScore !== undefined;
  const currentScore = goal.currentScore ?? 0;
  const fillPercent = Math.min((currentScore / goal.targetScore) * 100, 100);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title={goal.sopTitle}
      snapTo="full"
    >
      <div className="space-y-6 pb-4">
        {/* Status badge for completed/abandoned */}
        {!isActive && (
          <div className="flex justify-center">
            {isCompleted && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700">
                <CheckCircle2 size={14} strokeWidth={1.5} />
                Completed
              </span>
            )}
            {isAbandoned && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-600">
                Abandoned
              </span>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* PROGRESS SECTION                            */}
        {/* ============================================ */}
        <div className="rounded-2xl bg-gray-50 p-4">
          {/* Score display */}
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-op-text-secondary">Current</p>
              {hasScore ? (
                <p className={cn('text-xl font-bold', getScoreColor(goal.currentScore, 'text'))}>
                  {getRubricLabel(currentScore)}
                </p>
              ) : (
                <p className="text-lg font-semibold text-op-text-tertiary">Not yet assessed</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-op-text-secondary">Target</p>
              <p className="text-xl font-bold text-op-text-primary">
                {getRubricLabel(goal.targetScore)}
              </p>
            </div>
          </div>

          {/* Progress bar (h-3, larger than card's h-2) */}
          <div className="mb-2">
            <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  isCompleted
                    ? 'bg-emerald-500'
                    : getProgressBarBg(goal.currentScore),
                )}
                style={{ width: `${isCompleted ? 100 : fillPercent}%` }}
              />
            </div>
          </div>

          {/* Score arrow + trend */}
          <div className="flex items-center justify-between">
            {hasScore && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-op-text-secondary">
                <span className={getScoreColor(goal.currentScore, 'text')}>{getRubricLabel(currentScore)}</span>
                <ArrowRight size={12} className="text-op-text-tertiary" />
                <span>{getRubricLabel(goal.targetScore)}</span>
              </span>
            )}
            {goal.trend !== null && goal.trend !== undefined && (
              <TrendIndicator trend={goal.trend} />
            )}
          </div>

          {/* Category indicator */}
          {goal.category && (
            <div className="mt-2">
              <span className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize',
                getCategoryBadge(goal.category),
              )}>
                {goal.category}
              </span>
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* ASSESSMENT HISTORY SECTION                  */}
        {/* ============================================ */}
        <div>
          <div className="mb-3 flex items-center gap-2">
            <History size={16} strokeWidth={1.5} className="text-op-text-secondary" />
            <h3 className="text-sm font-semibold text-op-text-primary">Assessment History</h3>
          </div>

          {isLoading ? (
            <HistorySkeleton />
          ) : isError ? (
            <div className="rounded-xl bg-red-50 px-4 py-6 text-center">
              <AlertCircle size={24} className="mx-auto text-red-400" />
              <p className="mt-2 text-sm font-medium text-red-700">Could not load history</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-3 min-h-[44px] rounded-xl bg-red-100 px-4 py-2 text-sm font-semibold text-red-700 active:bg-red-200 transition-colors duration-150"
              >
                Retry
              </button>
            </div>
          ) : history.length === 0 ? (
            <div className="rounded-xl bg-gray-50 px-4 py-8 text-center">
              <Target size={28} className="mx-auto text-op-text-tertiary" />
              <p className="mt-3 text-sm font-medium text-op-text-secondary">
                No assessments yet
              </p>
              <p className="mt-1 text-xs text-op-text-tertiary">
                Your scores will appear here after your next assessment.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 overflow-hidden rounded-xl bg-gray-50 ring-1 ring-black/[0.04]">
              {history.map((item) => (
                <HistoryRow key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* ACTIONS SECTION (active goals only)         */}
        {/* ============================================ */}
        {isActive && (
          <div className="space-y-3">
            {/* Change Target */}
            {actionMode === 'editTarget' ? (
              <div className="rounded-2xl bg-gray-50 p-4">
                <p className="mb-2 text-sm font-semibold text-op-text-primary">
                  Set your target
                </p>
                <div
                  role="radiogroup"
                  aria-label="Target level"
                  className="mb-3 flex flex-wrap gap-2"
                >
                  {TARGET_OPTIONS.map((opt) => {
                    const selected = editTarget === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        aria-label={opt.label}
                        className={cn(
                          'min-h-[44px] whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150',
                          selected
                            ? 'bg-guild-green text-white'
                            : 'bg-white text-op-text-secondary active:bg-gray-200 ring-1 ring-black/[0.04]',
                          'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
                        )}
                        onClick={() => setEditTarget(opt.value)}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActionMode('none')}
                    className="min-h-[44px] flex-1 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-op-text-secondary ring-1 ring-black/[0.04] active:bg-gray-100 transition-colors duration-150"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={editTarget === goal.targetScore || updateGoal.isPending}
                    onClick={handleSaveTarget}
                    className={cn(
                      'min-h-[44px] flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition-colors duration-150',
                      editTarget !== goal.targetScore && !updateGoal.isPending
                        ? 'bg-guild-green text-white active:bg-guild-green/90'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed',
                    )}
                  >
                    {updateGoal.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleStartEditTarget}
                className="min-h-[44px] w-full rounded-xl bg-gray-50 px-4 py-3 text-sm font-semibold text-op-text-primary ring-1 ring-black/[0.04] active:bg-gray-100 transition-colors duration-150"
              >
                Change Target
              </button>
            )}

            {/* Abandon Goal */}
            {actionMode === 'confirmAbandon' ? (
              <div className="rounded-2xl bg-amber-50 p-4">
                <p className="mb-3 text-sm font-medium text-amber-800">
                  Are you sure you want to abandon this goal? You can always create a new one later.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActionMode('none')}
                    className="min-h-[44px] flex-1 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-op-text-secondary ring-1 ring-black/[0.04] active:bg-gray-100 transition-colors duration-150"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={updateGoal.isPending}
                    onClick={handleAbandon}
                    className="min-h-[44px] flex-1 rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white active:bg-amber-700 transition-colors duration-150"
                  >
                    {updateGoal.isPending ? 'Abandoning...' : 'Abandon'}
                  </button>
                </div>
              </div>
            ) : actionMode !== 'editTarget' ? (
              <button
                type="button"
                onClick={() => setActionMode('confirmAbandon')}
                className="min-h-[44px] w-full rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 ring-1 ring-amber-200 active:bg-amber-100 transition-colors duration-150"
              >
                Abandon Goal
              </button>
            ) : null}

            {/* Delete Goal */}
            {actionMode === 'confirmDelete' ? (
              <div className="rounded-2xl bg-red-50 p-4">
                <p className="mb-3 text-sm font-medium text-red-800">
                  This cannot be undone. Your goal and its progress tracking will be permanently removed.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActionMode('none')}
                    className="min-h-[44px] flex-1 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-op-text-secondary ring-1 ring-black/[0.04] active:bg-gray-100 transition-colors duration-150"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={deleteGoal.isPending}
                    onClick={handleDelete}
                    className="min-h-[44px] flex-1 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white active:bg-red-700 transition-colors duration-150"
                  >
                    {deleteGoal.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ) : actionMode !== 'editTarget' && actionMode !== 'confirmAbandon' ? (
              <button
                type="button"
                onClick={() => setActionMode('confirmDelete')}
                className="min-h-[44px] w-full rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600 ring-1 ring-red-200 active:bg-red-100 transition-colors duration-150"
              >
                Delete Goal
              </button>
            ) : null}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

// ============================================
// HISTORY ROW
// ============================================

function HistoryRow({ item }: { item: GoalAssessmentHistoryItem }) {
  const formattedDate = dateFormatter.format(new Date(item.assessedAt));
  const scoreColorClass = getScoreColor(item.relevantScore, 'text');

  return (
    <div className="flex items-center justify-between px-4 py-3">
      {/* Left: date + section */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-op-text-primary">{formattedDate}</p>
        {item.sectionTitle && (
          <p className="mt-0.5 text-xs text-op-text-tertiary line-clamp-1">
            {item.sectionTitle}
          </p>
        )}
      </div>

      {/* Right: score + delta */}
      <div className="flex items-center gap-2">
        <div className="text-right">
          <span className={cn('text-sm font-semibold', scoreColorClass)}>
            {getRubricLabel(item.relevantScore)}
          </span>
        </div>
        {item.delta !== null && (
          <DeltaBadge delta={item.delta} />
        )}
      </div>
    </div>
  );
}

// ============================================
// DELTA BADGE
// ============================================

function DeltaBadge({ delta }: { delta: number }) {
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
        <TrendingUp size={10} strokeWidth={2} />
        +{delta}
      </span>
    );
  }

  if (delta < 0) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-700">
        <TrendingDown size={10} strokeWidth={2} />
        {delta}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-500">
      <Minus size={10} strokeWidth={2} />
      0
    </span>
  );
}

// ============================================
// TREND INDICATOR (reused from GoalCard)
// ============================================

function TrendIndicator({ trend }: { trend: number }) {
  if (trend >= 5) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
        <TrendingUp size={14} strokeWidth={1.5} />
        Improving
      </span>
    );
  }

  if (trend <= -5) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
        <TrendingDown size={14} strokeWidth={1.5} />
        Declining
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-op-text-tertiary">
      <Minus size={14} strokeWidth={1.5} />
      Steady
    </span>
  );
}

// ============================================
// HELPERS
// ============================================

/** Progress bar background color (literal strings for Tailwind purge) */
function getProgressBarBg(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'bg-gray-200';
  if (score >= 80) return 'bg-emerald-600';
  if (score >= 60) return 'bg-amber-600';
  return 'bg-red-600';
}

/** Category badge color classes */
function getCategoryBadge(category: string): string {
  switch (category) {
    case 'safety':
      return 'bg-red-100 text-red-700';
    case 'efficiency':
      return 'bg-blue-100 text-blue-700';
    case 'compliance':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

// ============================================
// LOADING SKELETONS
// ============================================

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      {/* Progress section */}
      <div className="rounded-2xl bg-gray-50 p-4">
        <div className="mb-3 flex justify-between">
          <div className="space-y-2">
            <div className="h-3 w-20 rounded bg-gray-200" />
            <div className="h-7 w-16 rounded bg-gray-200" />
          </div>
          <div className="space-y-2 text-right">
            <div className="ml-auto h-3 w-16 rounded bg-gray-200" />
            <div className="ml-auto h-7 w-12 rounded bg-gray-200" />
          </div>
        </div>
        <div className="h-3 w-full rounded-full bg-gray-200" />
      </div>
      {/* History section */}
      <div>
        <div className="mb-3 h-4 w-36 rounded bg-gray-200" />
        <HistorySkeleton />
      </div>
    </div>
  );
}

function HistorySkeleton() {
  return (
    <div className="divide-y divide-gray-100 overflow-hidden rounded-xl bg-gray-50 ring-1 ring-black/[0.04]">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center justify-between px-4 py-3">
          <div className="space-y-1.5">
            <div className="h-3.5 w-16 rounded bg-gray-200" />
            <div className="h-2.5 w-24 rounded bg-gray-200" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-20 rounded bg-gray-200" />
            <div className="h-5 w-10 rounded-full bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  );
}
